import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/api";
import { AppShell } from "@/components/AppShell";
import { WhatsAppSendDialog } from "@/components/WhatsAppSendDialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, FileText, MessageCircle, RefreshCw } from "lucide-react";

const COLS = [
  ["item", "Item"],
  ["shade", "Shade"],
  ["centre", "Centre"],
  ["than_stock", "Than Stock"],
  ["min_stock", "Minimum"],
  ["shortfall", "Shortfall"],
  ["on_order", "On Order"],
  ["to_order", "To Order"],
  ["purchase_rate", "Purchase Rate"],
  ["supplier", "Supplier"],
];

export default function Reorder() {
  const [data, setData] = useState(null);
  const [profile, setProfile] = useState(null);
  const [includePending, setIncludePending] = useState(true);
  const [waOpen, setWaOpen] = useState(false);
  const [waMessage, setWaMessage] = useState("");
  const [waSupplier, setWaSupplier] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/reorder", { params: { include_pending: includePending } });
      setData(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, [includePending]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/profile").then(({ data }) => setProfile(data)).catch(() => {}); }, []);

  const download = async (format) => {
    try {
      const res = await api.get("/reorder/export", {
        params: { format, include_pending: includePending },
        responseType: "blob",
      });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = format === "pdf" ? "reorder-list.pdf" : "reorder-list.xlsx";
      a.click();
    } catch {
      toast.error("Could not build the file");
    }
  };

  const shareGroup = (g) => {
    const lines = g.rows
      .filter((r) => r.to_order > 0)
      .map((r) => `${r.item}${r.shade ? ` / ${r.shade}` : ""} — ${r.to_order} than`);
    setWaSupplier(g.rows.find((r) => r.supplier)?.supplier || "");
    setWaMessage(
      `Namaste, please arrange the following from ${g.company}:\n\n${lines.join("\n")}\n\nTotal ${g.to_order} than. Kindly confirm dispatch.`
    );
    setWaOpen(true);
  };

  return (
    <AppShell profile={profile}>
      <main className="mx-auto max-w-[1400px] px-4 py-8 sm:px-8" data-testid="reorder-page">
        <div className="mod-band rounded-sm px-5 py-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--mod-primary)]">Stock alert</p>
          <h1 className="font-head mt-2 text-4xl font-extrabold tracking-tighter sm:text-5xl">
            Low stock & reorder
          </h1>
          <p className="mt-1 text-sm text-zinc-600">
            Shades below their minimum, grouped by company. Quantity already on order with the mill is deducted.
          </p>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-2">
          <Button variant={includePending ? "default" : "outline"} size="sm" className="rounded-sm text-xs"
            data-testid="reorder-pending-toggle" onClick={() => setIncludePending((p) => !p)}>
            {includePending ? "Net of mill orders" : "Ignoring mill orders"}
          </Button>
          <Button variant="outline" size="sm" className="rounded-sm text-xs" data-testid="reorder-refresh-btn"
            onClick={load}>
            <RefreshCw size={13} className="mr-1.5" /> Refresh
          </Button>
          <Button variant="outline" size="sm" className="rounded-sm text-xs" data-testid="reorder-excel-btn"
            onClick={() => download("excel")}>
            <Download size={13} className="mr-1.5" /> Excel
          </Button>
          <Button variant="outline" size="sm" className="rounded-sm text-xs" data-testid="reorder-pdf-btn"
            onClick={() => download("pdf")}>
            <FileText size={13} className="mr-1.5" /> PDF
          </Button>
        </div>

        {!data ? (
          <p className="py-12 text-center text-sm text-zinc-500">Loading…</p>
        ) : (
          <>
            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="reorder-stats">
              {[
                ["Shades below minimum", data.totals.lines, "reorder-stat-lines"],
                ["Shortfall (than)", data.totals.shortfall, "reorder-stat-shortfall"],
                ["Already on order", data.totals.on_order, "reorder-stat-onorder"],
                ["To order (than)", data.totals.to_order, "reorder-stat-toorder"],
              ].map(([label, v, tid]) => (
                <div key={label} className="border border-zinc-200 bg-white px-3 py-2.5">
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">{label}</p>
                  <p className="mono text-lg font-bold" data-testid={tid}>{v}</p>
                </div>
              ))}
            </div>

            {data.groups.length === 0 ? (
              <div data-testid="reorder-empty" className="mt-6 border border-dashed border-zinc-300 p-12 text-center">
                <h3 className="font-head text-xl font-bold tracking-tight">Nothing to reorder</h3>
                <p className="mt-1 text-sm text-zinc-500">
                  Every shade with a minimum set is above it. Set minimums in Item Master to see alerts here.
                </p>
              </div>
            ) : (
              data.groups.map((g) => (
                <section key={g.company} className="mt-7" data-testid={`reorder-group-${g.company}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b-2 border-zinc-950 pb-2">
                    <div>
                      <h2 className="font-head text-lg font-extrabold tracking-tight">{g.company}</h2>
                      <p className="text-xs text-zinc-500">
                        {g.rows.length} shade(s) · to order {g.to_order} than · approx ₹{g.value}
                      </p>
                    </div>
                    <Button size="sm" className="rounded-sm bg-[#128C7E] text-xs text-white hover:bg-[#0f7568]"
                      data-testid={`reorder-wa-${g.company}`} onClick={() => shareGroup(g)}>
                      <MessageCircle size={13} className="mr-1.5" /> WhatsApp list
                    </Button>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-sm">
                      <thead>
                        <tr className="bg-zinc-100 text-left">
                          {COLS.map(([k, label]) => (
                            <th key={k} className="border-b border-zinc-200 px-2 py-2 text-[10px] font-bold uppercase tracking-wide">
                              {label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {g.rows.map((r, i) => (
                          <tr key={`${r.item}-${r.shade}-${i}`} className="odd:bg-white even:bg-zinc-50"
                            data-testid={`reorder-row-${r.item}-${r.shade}`}>
                            {COLS.map(([k]) => (
                              <td key={k} className={`border-b border-zinc-100 px-2 py-1.5 ${
                                ["than_stock", "min_stock", "shortfall", "on_order", "to_order", "purchase_rate"].includes(k)
                                  ? "mono text-right" : ""
                              } ${k === "to_order" && r.to_order > 0 ? "font-bold text-rose-600" : ""}`}>
                                {String(r[k] ?? "").trim() === "" ? "—" : r[k]}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              ))
            )}
          </>
        )}
      </main>

      <WhatsAppSendDialog
        open={waOpen}
        onOpenChange={setWaOpen}
        title="Send shortage list to supplier"
        contactsPath="/suppliers/contacts"
        contactLabel="Supplier"
        defaultCustomer={waSupplier}
        message={waMessage}
      />
    </AppShell>
  );
}
